import { parseLevel, type LevelDef, type LevelObject } from '../sim/level/schema';

export type ToolGroup = 'Terrain' | 'Traps' | 'Toys' | 'Spawns';
export type Piece = {
  id: string;
  name: string;
  icon: string;
  group: ToolGroup;
  hint: string;
  defaults?: Partial<LevelObject>;
};
export const PIECES: Piece[] = [
  {
    id: 'solid',
    name: 'Ground',
    icon: '▰',
    group: 'Terrain',
    hint: 'Solid footing. Drag to build a path.',
    defaults: { w: 2, h: 1 },
  },
  {
    id: 'ice',
    name: 'Ice',
    icon: '❄',
    group: 'Terrain',
    hint: 'Slippery ground keeps fighters sliding.',
  },
  {
    id: 'platform.moving',
    name: 'Moving ledge',
    icon: '↔',
    group: 'Terrain',
    hint: 'A platform that glides back and forth.',
    defaults: { w: 4, h: 0.6, speed: 3 },
  },
  {
    id: 'platform.rotating',
    name: 'Spinner',
    icon: '⟳',
    group: 'Terrain',
    hint: 'A spinning platform.',
    defaults: { w: 5, h: 0.6 },
  },
  {
    id: 'platform.disappearing',
    name: 'Blink ledge',
    icon: '◌',
    group: 'Terrain',
    hint: 'Appears and disappears on a timer.',
    defaults: { w: 4, h: 0.6 },
  },
  {
    id: 'platform.collapsing',
    name: 'Crumble ledge',
    icon: '▧',
    group: 'Terrain',
    hint: 'Falls shortly after someone stands on it.',
    defaults: { w: 4, h: 0.6 },
  },
  {
    id: 'platform.momentum',
    name: 'Teeter board',
    icon: '⚖',
    group: 'Terrain',
    hint: 'Tilts under the weight of fighters.',
    defaults: { w: 5, h: 0.6 },
  },
  {
    id: 'block.destructible',
    name: 'Breakable',
    icon: '▦',
    group: 'Terrain',
    hint: 'A block that can be broken.',
    defaults: { w: 2, h: 2 },
  },
  {
    id: 'spikes',
    name: 'Spikes',
    icon: '▲',
    group: 'Traps',
    hint: 'Sharp ground. Leave a way around it.',
  },
  {
    id: 'lava',
    name: 'Lava',
    icon: '≈',
    group: 'Traps',
    hint: 'A pool of danger.',
    defaults: { w: 4, h: 1 },
  },
  {
    id: 'saw',
    name: 'Saw',
    icon: '✹',
    group: 'Traps',
    hint: 'A spinning circular blade.',
    defaults: { r: 0.65, w: 1.3, h: 1.3 },
  },
  {
    id: 'laser',
    name: 'Laser',
    icon: '→',
    group: 'Traps',
    hint: 'A timed beam firing to the right.',
    defaults: { w: 8, h: 0.3 },
  },
  {
    id: 'spikeball',
    name: 'Spike ball',
    icon: '✺',
    group: 'Traps',
    hint: 'A heavy ball with a dangerous edge.',
    defaults: { r: 0.7, w: 1.4, h: 1.4 },
  },
  {
    id: 'crusher',
    name: 'Crusher',
    icon: '⬇',
    group: 'Traps',
    hint: 'Drops from above, then rises again.',
    defaults: { w: 3, h: 2 },
  },
  {
    id: 'crate',
    name: 'Crate',
    icon: '⊠',
    group: 'Toys',
    hint: 'A loose box to push and throw.',
    defaults: { w: 1.5, h: 1.5 },
  },
  {
    id: 'barrel.explosive',
    name: 'Boom barrel',
    icon: '●',
    group: 'Toys',
    hint: 'An explosive surprise.',
    defaults: { w: 1, h: 1.5 },
  },
  {
    id: 'bounce',
    name: 'Spring',
    icon: '⇧',
    group: 'Toys',
    hint: 'Launches fighters into the air.',
  },
  {
    id: 'conveyor',
    name: 'Conveyor',
    icon: '»',
    group: 'Toys',
    hint: 'Carries fighters to the right.',
    defaults: { w: 4, h: 0.6 },
  },
  {
    id: 'chain',
    name: 'Chain',
    icon: '⛓',
    group: 'Toys',
    hint: 'A hanging, breakable chain.',
    defaults: { links: 5 },
  },
  {
    id: 'trigger.drop',
    name: 'Weapon gift',
    icon: '↓',
    group: 'Toys',
    hint: 'Drops a pistol after three seconds.',
    defaults: { weapon: 'pistol', atTick: 180 },
  },
  {
    id: 'spawn',
    name: 'Player start',
    icon: '⚑',
    group: 'Spawns',
    hint: 'Four starts support a small match; add eight for a full party. Pick up any flag to move it.',
  },
  {
    id: 'drop-range',
    name: 'Drop zone',
    icon: '⇣',
    group: 'Spawns',
    hint: 'Place the left or right edge of random weapon drops.',
  },
];
export const PALETTE = PIECES.filter((p) => p.id !== 'spawn' && p.id !== 'drop-range').map(
  (p) => p.id,
);
export const EXTRA_TOOLS = ['spawn', 'drop-range'] as const;
export type EditorTool = string;
export type EditorState = {
  level: LevelDef;
  selected: number;
  selectedSpawn: number;
  /** Object indices in the current group; flags remain individually editable. */
  selection?: number[];
  tool: EditorTool;
  grid: number;
  history: LevelDef[];
  future: LevelDef[];
  gesture?: LevelDef;
};
const DRAFT_KEY = 'floppy-clash-maker-draft';

export function freshArena(size: 'compact' | 'large' = 'compact'): LevelDef {
  if (size === 'large') {
    return {
      id: 'user-draft', name: 'My large arena', theme: 'woods',
      bounds: { x: 0, y: 0, w: 48, h: 20 }, killMargin: 6,
      spawns: [4, 44, 10, 38, 16, 32, 22, 26].map((x) => ({ x, y: 4 })),
      drops: { enabled: true, xMin: 3, xMax: 45, intervalScale: 1 },
      objects: [{ type: 'solid', x: 24, y: 1, w: 48, h: 2 }],
    };
  }
  return {
    id: 'user-draft',
    name: 'My first arena',
    theme: 'woods',
    bounds: { x: 0, y: 0, w: 32, h: 18 },
    killMargin: 6,
    spawns: [
      { x: 5, y: 4 },
      { x: 27, y: 4 },
      { x: 11, y: 4 },
      { x: 21, y: 4 },
    ],
    drops: { enabled: true, xMin: 4, xMax: 28, intervalScale: 1 },
    objects: [{ type: 'solid', x: 16, y: 1, w: 32, h: 2 }],
  };
}

export function createEditorState(): EditorState {
  let draft: LevelDef | null = null;
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) draft = validateEditorLevel(JSON.parse(saved));
  } catch {
    /* First visit or storage is unavailable. */
  }
  return {
    level: draft ?? freshArena(),
    selected: -1,
    selectedSpawn: -1,
    tool: 'solid',
    grid: 1,
    history: [],
    future: [],
  };
}
export function storeDraft(state: EditorState): boolean {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(state.level));
    return true;
  } catch {
    return false;
  }
}
export function pieceFor(tool: string): Piece {
  return PIECES.find((p) => p.id === tool) ?? PIECES[0]!;
}
export function snap(v: number, g: number): number {
  return Math.round(v / g) * g;
}
export function beginGesture(state: EditorState): void {
  if (!state.gesture) state.gesture = structuredClone(state.level);
}
export function commitGesture(state: EditorState): boolean {
  const before = state.gesture;
  delete state.gesture;
  if (!before || JSON.stringify(before) === JSON.stringify(state.level)) return false;
  state.history.push(before);
  if (state.history.length > 100) state.history.shift();
  state.future = [];
  return true;
}
/** Restore an unfinished move or paint stroke without consuming Undo or Redo. */
export function cancelGesture(state: EditorState): boolean {
  const before = state.gesture;
  delete state.gesture;
  if (!before) return false;
  state.level = before;
  if (state.selected >= before.objects.length) state.selected = -1;
  if (state.selectedSpawn >= before.spawns.length) state.selectedSpawn = -1;
  state.selection = state.selection?.filter((i) => i < before.objects.length);
  return true;
}
export function editLevel(state: EditorState, edit: () => void): void {
  const own = !state.gesture;
  if (own) beginGesture(state);
  edit();
  if (own) commitGesture(state);
}
export function pushHistory(state: EditorState): void {
  if (state.gesture) return;
  state.history.push(structuredClone(state.level));
  if (state.history.length > 100) state.history.shift();
  state.future = [];
}
function clearSelection(state: EditorState): void {
  state.selected = -1;
  state.selectedSpawn = -1;
  state.selection = [];
}
export function undo(state: EditorState): void {
  commitGesture(state);
  const prev = state.history.pop();
  if (!prev) return;
  state.future.push(structuredClone(state.level));
  state.level = prev;
  clearSelection(state);
}
export function redo(state: EditorState): void {
  commitGesture(state);
  const next = state.future.pop();
  if (!next) return;
  state.history.push(structuredClone(state.level));
  state.level = next;
  clearSelection(state);
}
export function makeObject(tool: string, x: number, y: number, grid = 1): LevelObject {
  return { type: tool, x: snap(x, grid), y: snap(y, grid), w: 2, h: 1, ...pieceFor(tool).defaults };
}
export const MAX_EDITOR_SPAWNS = 32;
export type PlacementResult = 'placed' | 'duplicate' | 'spawn-limit';

/** Normalize omitted geometry defaults and property ordering before comparing an existing stamp. */
function pieceSignature(obj: LevelObject): string {
  const normalized = { ...obj, w: obj.w ?? 2, h: obj.h ?? 1, angle: obj.angle ?? 0 };
  return JSON.stringify(
    Object.entries(normalized)
      .filter(([, value]) => value !== undefined)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}
export function addObject(state: EditorState, x: number, y: number): PlacementResult {
  if (state.tool === 'spawn') return addSpawn(state, x, y);
  if (state.tool === 'drop-range') {
    setDropEdge(state, x);
    return 'placed';
  }
  const candidate = makeObject(state.tool, x, y, state.grid),
    signature = pieceSignature(candidate);
  const existing = state.level.objects.findIndex((obj) => pieceSignature(obj) === signature);
  if (existing >= 0) {
    state.selected = existing;
    state.selection = [existing];
    state.selectedSpawn = -1;
    return 'duplicate';
  }
  editLevel(state, () => {
    state.level.objects.push(candidate);
    state.selected = state.level.objects.length - 1;
    state.selection = [state.selected];
    state.selectedSpawn = -1;
  });
  return 'placed';
}
export function addSpawn(state: EditorState, x: number, y: number): PlacementResult {
  const spawn = { x: snap(x, state.grid), y: snap(y, state.grid) };
  const existing = state.level.spawns.findIndex((p) => p.x === spawn.x && p.y === spawn.y);
  if (existing >= 0) {
    state.selectedSpawn = existing;
    state.selected = -1;
    return 'duplicate';
  }
  if (state.level.spawns.length >= MAX_EDITOR_SPAWNS) return 'spawn-limit';
  editLevel(state, () => {
    state.level.spawns.push(spawn);
    state.selectedSpawn = state.level.spawns.length - 1;
    state.selected = -1;
  });
  return 'placed';
}
export function moveSpawn(state: EditorState, i: number, x: number, y: number): void {
  const s = state.level.spawns[i];
  if (!s) return;
  editLevel(state, () => {
    s.x = snap(x, state.grid);
    s.y = snap(y, state.grid);
  });
}
export function setDropEdge(state: EditorState, x: number): void {
  editLevel(state, () => {
    const sx = snap(x, state.grid);
    const drops = state.level.drops ?? { enabled: true, xMin: 4, xMax: 28, intervalScale: 1 };
    if (sx < (drops.xMin + drops.xMax) / 2) drops.xMin = sx;
    else drops.xMax = sx;
    if (drops.xMin > drops.xMax) [drops.xMin, drops.xMax] = [drops.xMax, drops.xMin];
    state.level.drops = drops;
  });
}
export function selectSpawnAt(state: EditorState, x: number, y: number): number {
  for (let i = state.level.spawns.length - 1; i >= 0; i--) {
    const s = state.level.spawns[i]!;
    if (Math.hypot(s.x - x, s.y - y) < 0.8) {
      state.selectedSpawn = i;
      state.selected = -1;
      return i;
    }
  }
  return -1;
}
export function selectionIndices(state: EditorState): number[] {
  if (state.selectedSpawn >= 0 || !state.level.objects[state.selected]) return [];
  const selected = state.selection?.includes(state.selected) ? state.selection : [state.selected];
  return [...new Set(selected)].filter((i) => !!state.level.objects[i]);
}
export function selectObjects(state: EditorState, indices: number[]): void {
  state.selection = [...new Set(indices)].filter((i) => !!state.level.objects[i]);
  state.selected = state.selection.at(-1) ?? -1;
  state.selectedSpawn = -1;
}
export function toggleObjectSelection(state: EditorState, index: number): void {
  const indices = selectionIndices(state);
  selectObjects(
    state,
    indices.includes(index) ? indices.filter((i) => i !== index) : [...indices, index],
  );
}
export function selectObjectsInBox(
  state: EditorState,
  a: { x: number; y: number },
  b: { x: number; y: number },
  previous: number[] = [],
): void {
  const indices = state.level.objects.flatMap((obj, i) =>
    obj.x >= Math.min(a.x, b.x) &&
    obj.x <= Math.max(a.x, b.x) &&
    obj.y >= Math.min(a.y, b.y) &&
    obj.y <= Math.max(a.y, b.y)
      ? [i]
      : [],
  );
  selectObjects(state, [...previous, ...indices]);
}
export function moveSelected(state: EditorState, x: number, y: number): void {
  const obj = state.level.objects[state.selected];
  if (!obj) return;
  editLevel(state, () => {
    const dx = snap(x - obj.x, state.grid),
      dy = snap(y - obj.y, state.grid);
    for (const i of selectionIndices(state)) {
      const member = state.level.objects[i]!;
      member.x += dx;
      member.y += dy;
      member.path = member.path?.map((p) => ({ x: p.x + dx, y: p.y + dy }));
    }
  });
}
export type ResizeCorner = { x: -1 | 1; y: -1 | 1 };
export function resizeCorners(obj: LevelObject): { corner: ResizeCorner; x: number; y: number }[] {
  const angle = obj.angle ?? 0,
    c = Math.cos(angle),
    s = Math.sin(angle);
  return ([-1, 1] as const).flatMap((x) =>
    ([-1, 1] as const).map((y) => {
      const lx = (x * (obj.w ?? 2)) / 2,
        ly = (y * (obj.h ?? 1)) / 2;
      return { corner: { x, y }, x: obj.x + lx * c - ly * s, y: obj.y + lx * s + ly * c };
    }),
  );
}
/** Opposite corner stays fixed, including for a rotated piece. One pointer gesture owns history. */
export function resizeFromCorner(
  state: EditorState,
  original: LevelObject,
  corner: ResizeCorner,
  point: { x: number; y: number },
): void {
  const obj = state.level.objects[state.selected];
  if (!obj || selectionIndices(state).length !== 1) return;
  const angle = original.angle ?? 0,
    c = Math.cos(angle),
    s = Math.sin(angle);
  const anchor = resizeCorners(original).find(
    (p) => p.corner.x === -corner.x && p.corner.y === -corner.y,
  )!;
  const dx = point.x - anchor.x,
    dy = point.y - anchor.y;
  let w = Math.min(64, Math.max(state.grid, snap((dx * c + dy * s) * corner.x, state.grid)));
  let h = Math.min(36, Math.max(state.grid, snap((-dx * s + dy * c) * corner.y, state.grid)));
  if (obj.r !== undefined) w = h = Math.max(w, h);
  const lx = (corner.x * w) / 2,
    ly = (corner.y * h) / 2;
  editLevel(state, () => {
    const x = anchor.x + lx * c - ly * s,
      y = anchor.y + lx * s + ly * c;
    const offsetX = x - obj.x,
      offsetY = y - obj.y;
    obj.x = x;
    obj.y = y;
    obj.w = w;
    obj.h = h;
    if (obj.r !== undefined) obj.r = w / 2;
    obj.path = obj.path?.map((p) => ({ x: p.x + offsetX, y: p.y + offsetY }));
  });
}
export function resizeSelected(state: EditorState, w: number, h: number): void {
  const obj = state.level.objects[state.selected];
  if (!obj) return;
  editLevel(state, () => {
    obj.w = Math.min(64, Math.max(state.grid, snap(w, state.grid)));
    obj.h = Math.min(36, Math.max(state.grid, snap(h, state.grid)));
    if (obj.r !== undefined) obj.r = Math.max(obj.w, obj.h) / 2;
  });
}
export function rotateSelected(state: EditorState, delta: number): void {
  const obj = state.level.objects[state.selected];
  if (!obj) return;
  editLevel(state, () => {
    obj.angle = ((obj.angle ?? 0) + delta + Math.PI * 2) % (Math.PI * 2);
  });
}
export function duplicateSelected(
  state: EditorState,
): 'copied' | 'empty' | 'piece-limit' | 'spawn-limit' {
  const spawn = state.level.spawns[state.selectedSpawn],
    indices = selectionIndices(state);
  if (!spawn && !indices.length) return 'empty';
  if (spawn && state.level.spawns.length >= MAX_EDITOR_SPAWNS) return 'spawn-limit';
  if (indices.length + state.level.objects.length > 1200) return 'piece-limit';
  editLevel(state, () => {
    if (spawn) {
      state.level.spawns.push(structuredClone(spawn));
      state.selectedSpawn = state.level.spawns.length - 1;
      state.selected = -1;
      state.selection = [];
    } else {
      const base = state.level.objects.length,
        primary = indices.indexOf(state.selected);
      state.level.objects.push(...indices.map((i) => structuredClone(state.level.objects[i]!)));
      selectObjects(
        state,
        indices.map((_, i) => base + i),
      );
      state.selected = base + primary;
    }
  });
  return 'copied';
}
/** A copied group must move before dropping, so copies cannot stack invisibly. */
export function selectedHasDuplicate(state: EditorState): boolean {
  const spawn = state.level.spawns[state.selectedSpawn],
    indices = selectionIndices(state);
  return spawn
    ? state.level.spawns.some(
        (p, i) => i !== state.selectedSpawn && p.x === spawn.x && p.y === spawn.y,
      )
    : indices.some((index) =>
        state.level.objects.some(
          (p, i) =>
            !indices.includes(i) &&
            pieceSignature(p) === pieceSignature(state.level.objects[index]!),
        ),
      );
}
/** Test the visible footprint, including rotated rectangles, from front to back. */
function objectAt(state: EditorState, x: number, y: number): number {
  for (let i = state.level.objects.length - 1; i >= 0; i--) {
    const obj = state.level.objects[i]!,
      angle = -(obj.angle ?? 0);
    const dx = x - obj.x,
      dy = y - obj.y;
    const lx = dx * Math.cos(angle) - dy * Math.sin(angle),
      ly = dx * Math.sin(angle) + dy * Math.cos(angle);
    const hit =
      obj.r !== undefined
        ? Math.hypot(dx, dy) <= obj.r + 0.15
        : Math.abs(lx) <= (obj.w ?? 2) / 2 + 0.1 && Math.abs(ly) <= (obj.h ?? 1) / 2 + 0.1;
    if (hit) return i;
  }
  return -1;
}
export type EditorTarget = { kind: 'spawn' | 'object'; index: number };
/** Hover queries never change the selection or an in-progress manipulation. */
export function targetAt(state: EditorState, x: number, y: number): EditorTarget | null {
  for (let i = state.level.spawns.length - 1; i >= 0; i--) {
    const s = state.level.spawns[i]!;
    if (Math.hypot(s.x - x, s.y - y) < 0.8) return { kind: 'spawn', index: i };
  }
  const index = objectAt(state, x, y);
  return index < 0 ? null : { kind: 'object', index };
}
export function hitTest(state: EditorState, x: number, y: number): number {
  const index = objectAt(state, x, y);
  if (index >= 0) {
    if (!selectionIndices(state).includes(index)) state.selection = [index];
    state.selected = index;
    state.selectedSpawn = -1;
  }
  return index;
}
export const selectAt = hitTest;
export function eraseAt(
  state: EditorState,
  x: number,
  y: number,
): 'removed' | 'minimum-spawns' | 'empty' {
  if (selectSpawnAt(state, x, y) >= 0) {
    if (state.level.spawns.length <= 4) return 'minimum-spawns';
    editLevel(state, () => {
      state.level.spawns.splice(state.selectedSpawn, 1);
      clearSelection(state);
    });
    return 'removed';
  }
  if (hitTest(state, x, y) < 0) return 'empty';
  editLevel(state, () => {
    state.level.objects.splice(state.selected, 1);
    clearSelection(state);
  });
  return 'removed';
}
/** Imported arenas must fit the editor and remain safe to instantiate. */
export function validateEditorLevel(raw: unknown): LevelDef {
  const level = parseLevel(raw);
  if (level.bounds.w <= 0 || level.bounds.h <= 0 || level.bounds.w > 256 || level.bounds.h > 144)
    throw new Error('Arena dimensions must be between 1 and 256 × 144.');
  if (level.objects.length > 1200 || level.spawns.length > MAX_EDITOR_SPAWNS)
    throw new Error('This arena has too many pieces or player starts.');
  for (const obj of level.objects) {
    if (
      (obj.w !== undefined && obj.w <= 0) ||
      (obj.h !== undefined && obj.h <= 0) ||
      (obj.r !== undefined && obj.r <= 0)
    )
      throw new Error('Pieces need a positive size.');
    if (obj.links !== undefined && (obj.links < 0 || obj.links > 40))
      throw new Error('Chains can have up to 40 links.');
  }
  return level;
}
export function exportLevel(state: EditorState): string {
  return JSON.stringify(validateEditorLevel(state.level), null, 2);
}
export function importLevel(state: EditorState, raw: string): void {
  const parsed = validateEditorLevel(JSON.parse(raw));
  editLevel(state, () => {
    state.level = parsed;
    clearSelection(state);
  });
}
export function shareHash(state: EditorState): string {
  return btoa(unescape(encodeURIComponent(JSON.stringify(state.level))));
}
export function fromHash(hash: string): LevelDef | null {
  try {
    return validateEditorLevel(JSON.parse(decodeURIComponent(escape(atob(hash)))));
  } catch {
    return null;
  }
}
export async function saveLibrary(level: LevelDef): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('levels', 'readwrite');
      tx.objectStore('levels').put(structuredClone(level), level.id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}
export async function loadLibrary(): Promise<LevelDef[]> {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction('levels', 'readonly'),
        req = tx.objectStore('levels').getAll();
      req.onsuccess = () =>
        resolve(
          (req.result as unknown[]).flatMap((raw) => {
            try {
              return [validateEditorLevel(raw)];
            } catch {
              return [];
            }
          }),
        );
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('floppy-clash-levels', 1);
    req.onupgradeneeded = () => req.result.createObjectStore('levels');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export type EditorViewport = { x: number; y: number; scale: number; width: number; height: number };
export function editorViewport(
  bounds: LevelDef['bounds'],
  width: number,
  height: number,
): EditorViewport {
  return {
    x: bounds.x + bounds.w / 2,
    y: bounds.y + bounds.h / 2,
    scale: Math.min(width / bounds.w, height / bounds.h) * 0.94,
    width,
    height,
  };
}
/** Uses the same centered, letterboxed transform as the game renderer. */
export function screenToEditor(
  view: EditorViewport,
  px: number,
  py: number,
): { x: number; y: number } {
  return {
    x: view.x + (px - view.width / 2) / view.scale,
    y: view.y - (py - view.height / 2) / view.scale,
  };
}
